import React, { forwardRef, useLayoutEffect, useEffect, useRef } from 'react';
import type { ComponentProps, Ref } from 'react';
import HBBox from '../../components/HBBox/HBBox';
import HBFlex from '../../components/HBFlex/HBFlex';
import HBGrid from '../../components/HBGrid/HBGrid';

// `useLayoutEffect` warns when it runs during SSR (no DOM to measure/mutate).
// Fall back to a no-op `useEffect` on the server; the class is applied on
// the client after hydration instead.
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type WithStylesTarget = typeof HBBox | typeof HBFlex | typeof HBGrid;

/**
 * Internal-only escape hatch, for `HBBox`, `HBFlex`, and `HBGrid` — never
 * `HBSurface`. Prefer using all three layout primitives unstyled: reach for
 * this only when a region's CSS genuinely cannot be a first-class prop or a
 * static `style` value — real pseudo-classes or ancestor/descendant
 * selectors. See `src/styles/layout/AGENTS.md` § withStyles for the full
 * guidance and the decision order (props first, then a plain `HBBox` wrapper,
 * then this).
 *
 * All three primitives omit `className` from their consumer-facing props and
 * strip any runtime `className` before it reaches the DOM (defense in depth
 * against styled-components injecting one regardless of the declared type) —
 * so `styled(HBBox)`/`styled(HBFlex)`/`styled(HBGrid)` alone silently
 * produce dead CSS. `withStyles(Component)` wraps it in a component that
 * intercepts `className` *before* the primitive ever sees it, and applies it
 * directly to the rendered DOM node's `classList` via a ref — bypassing the
 * primitive's prop pipeline (and its strip) entirely.
 *
 * Usage: `styled(withStyles(HBFlex))\`...\`` in place of `styled(HBFlex)`.
 * This is for hbui's own internal implementation only — never export a
 * `withStyles`-wrapped component, or the className it re-enables, from any
 * public barrel.
 */
export function withStyles<C extends WithStylesTarget>(Component: C) {
    type Props = ComponentProps<C>;
    const AnyComponent = Component as unknown as React.ComponentType<
        Props & { ref?: Ref<HTMLElement> }
    >;

    const StyledCapableComponent = forwardRef<
        HTMLElement,
        Props & { className?: string }
    >(({ className, ...rest }, forwardedRef) => {
        const nodeRef = useRef<HTMLElement | null>(null);

        useIsomorphicLayoutEffect(() => {
            const node = nodeRef.current;
            if (!node || !className) {
                return undefined;
            }
            const classes = className.split(' ').filter(Boolean);
            if (classes.length === 0) {
                return undefined;
            }
            node.classList.add(...classes);
            return () => {
                node.classList.remove(...classes);
            };
        }, [className]);

        return (
            <AnyComponent
                {...(rest as Props)}
                ref={(node: HTMLElement | null) => {
                    nodeRef.current = node;
                    if (typeof forwardedRef === 'function') {
                        forwardedRef(node);
                    } else if (forwardedRef) {
                        (
                            forwardedRef as React.MutableRefObject<HTMLElement | null>
                        ).current = node;
                    }
                }}
            />
        );
    });

    StyledCapableComponent.displayName = `withStyles(${
        (Component as React.FC).displayName ?? 'Component'
    })`;

    return StyledCapableComponent as unknown as React.ForwardRefExoticComponent<
        Props & { className?: string; ref?: Ref<HTMLElement> }
    >;
}
